import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import ProductApi from 'apis/ProductApis';
import { ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterProduct from './FilterProduct';
import TableProduct from './TableProduct';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { exportExcel_v2 } from 'utils/constants/exportExcel';

interface User {
  isSale?: boolean;
  isCN?: boolean;
  plantCode?: string;
}

interface ProductType {
  name: string;
}

interface Product {
  ttype: ProductType;
  [key: string]: unknown;
}

interface PageInfo {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface ProductData {
  items: Product[];
  pageInfo: PageInfo;
}

interface FilterData {
  [key: string]: string | number | boolean | undefined;
}

interface MutationParams {
  types?: string;
  headerRow?: ColumnsTypeCustom;
  skip?: number;
  take?: number;
  [key: string]: unknown;
}

export default function ProductList() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any | undefined>();
  const tableRef = useRef<{ handleExportExcel: () => void } | null>(null);
  const [filterData, setFilterData] = useState<FilterData>({});
  const queryParam = useQueryParam();

  const page = parseInt(queryParam.get('page') || '1');
  const page_size = parseInt(queryParam.get('page_size') || '10');

  const defaultFilter = useMemo(() => {
    if (user?.isSale || user?.isCN)
      return {
        plantId: (user as User)?.plantCode,
      };

    return {};
  }, [user]);

  const { isPending: isLoading, mutate: refetch } = useMutation({
    mutationFn: (params: MutationParams) => {
      const mergedParams = { ...params, ...filterData, ...defaultFilter };

      return params?.types === 'export'
        ? ProductApi.getProduct(mergedParams)
        : ProductApi.getProduct({ ...params, ...defaultFilter });
    },
    onSuccess: (
      res: { data: { getAllProduct: ProductData } },
      variable: MutationParams,
    ) => {
      const data = res.data.getAllProduct;
      const items: Product[] = data.items.map((item: Product) => ({
        ...item,
        ttype: { name: item?.ttype?.name || '-' },
      }));

      const pageInfo: PageInfo = res.data.getAllProduct.pageInfo;
      const newData: ProductData = { items, pageInfo };

      if (variable.types === 'export') {
        exportExcel_v2(
          variable.headerRow || [],
          items,
          'Danh sách sản phẩm',
          true,
          2,
        );
      } else {
        setData(newData);
      }
    },
    onError: (error: Error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    user?.isCN && setFilterData({ ...defaultFilter });
  }, [user, defaultFilter]);

  useEffect(() => {
    const params: MutationParams = {
      skip: (page - 1) * page_size,
      take: page_size,
      ...filterData,
      ...defaultFilter,
    };

    refetch(params);
  }, [filterData, page, page_size, defaultFilter, refetch]);

  useEffect(() => {
    setTitle('Sản phẩm');
    setBreadcrumb([{ title: 'Sản phẩm' }]);
  }, [setTitle, setBreadcrumb]);

  const handleOkFiltered = (values: FilterData) => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData(values);
  };

  const handleCancelFiltered = () => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData({});
  };

  const handleExportExcel = useCallback(() => {
    if (tableRef.current) tableRef.current.handleExportExcel();
  }, []);

  return (
    <>
      <Row gutter={[8, 16]} align="middle" justify="end">
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportExcel} />
        </Col>
      </Row>
      {isOpenFilter && (
        <FilterProduct
          defaultFilter={defaultFilter}
          handleOkFiltered={handleOkFiltered}
          handleCancelFiltered={handleCancelFiltered}
        />
      )}
      <TableProduct
        data={data}
        ref={tableRef}
        isLoading={isLoading}
        refetch={refetch}
        filterData={filterData}
      />
    </>
  );
}
